<template>
  <div class="market-chat-page">
    <div class="chat-container">
      <div class="chat-header">
        <div class="chat-header-left">
          <h1 class="page-title">Market Chat</h1>
          <div class="chat-thread-controls">
            <div class="thread-dropdown-wrapper">
              <button class="thread-selector-btn" @click.stop="showThreadMenu = !showThreadMenu">
                <span class="thread-selector-name">{{ chatStore.activeThread?.name || 'Default' }}</span>
                <span class="thread-selector-count">{{ chatStore.threads.length }}</span>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="chevron">
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>
              <div v-if="showThreadMenu" class="thread-dropdown" @click.stop>
                <div class="thread-dropdown-section">
                  <button
                    v-for="thread in chatStore.threads"
                    :key="thread.id"
                    :class="['thread-dropdown-item', { active: thread.id === chatStore.activeThreadId }]"
                    @click="switchToThread(thread.id)"
                  >
                    <span class="thread-dropdown-item-name">{{ thread.name }}</span>
                    <span class="thread-dropdown-item-count">{{ thread.messages.length }}</span>
                    <button
                      v-if="thread.id !== 'default'"
                      class="thread-delete-btn"
                      @click.stop="handleDeleteThread(thread.id)"
                    >&times;</button>
                  </button>
                </div>
              </div>
            </div>
            <button class="new-thread-btn" @click="handleNewThread">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              New
            </button>
          </div>
        </div>
        <div class="chat-settings">
          <select v-model="chatStore.config.scope" class="setting-select">
            <option value="overview">Overview</option>
            <option value="gold">Gold</option>
            <option value="silver">Silver</option>
            <option value="copper">Copper</option>
            <option value="bitcoin">Bitcoin</option>
            <option value="fertilizer">Fertilizer</option>
            <option value="crude_oil">Crude Oil</option>
            <option value="rice">Rice</option>
            <option value="dairy">Dairy</option>
            <option value="salt">Salt</option>
            <option value="edible_oil">Edible Oil</option>
            <option value="sugar">Sugar</option>
            <option value="tea">Tea</option>
          </select>
        </div>
      </div>

      <div class="messages-container" ref="messagesContainer">
        <div
          v-for="(message, index) in chatStore.messages"
          :key="index"
          :class="['message', message.role]"
        >
          <div class="message-avatar">
            {{ message.role === 'user' ? 'U' : message.role === 'assistant' ? 'AI' : '!' }}
          </div>
          <div class="message-content">
            <div v-if="message.role === 'assistant'" v-html="renderedContent(message.content)" class="message-text markdown"></div>
            <div v-else class="message-text">{{ message.content }}</div>
            <div v-if="message.chart || (message.charts && message.charts.length)" class="message-charts">
              <ChartWrapper
                v-if="message.chart"
                :chart-id="`chart-${message.id}`"
                :type="message.chart.type || 'line'"
                :data="chartToChartData(message.chart)"
                :title="message.chart.title || ''"
              />
              <ChartWrapper
                v-for="(ch, ci) in (message.charts || [])"
                :key="ci"
                :chart-id="`chart-${message.id}-${ci}`"
                :type="ch.type || 'line'"
                :data="chartToChartData(ch)"
                :title="ch.title || ''"
              />
            </div>
            <div v-if="message.progressLines && message.progressLines.length" class="msg-progress">
              <div class="msg-progress-header" @click="toggleMsgProgress(message.id)">
                <span>Agent Progress ({{ message.progressLines.length }})</span>
                <svg :class="['progress-chevron', { open: expandedMsgProgress[message.id] }]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </div>
              <div v-if="expandedMsgProgress[message.id]" class="msg-progress-lines">
                <div v-for="(line, li) in message.progressLines" :key="li" class="msg-progress-line">
                  <span class="msg-progress-time">{{ line.time }}</span>
                  <span class="msg-progress-text">{{ line.text }}</span>
                </div>
              </div>
            </div>
            <span class="message-time">{{ formatTime(message.timestamp) }}</span>
          </div>
        </div>

        <div v-if="chatStore.streamingMessage" class="message assistant streaming">
          <div class="message-avatar">AI</div>
          <div class="message-content">
            <div v-html="renderedContent(chatStore.streamingMessage)" class="message-text markdown"></div>
          </div>
        </div>

        <div v-if="chatStore.isLoading && !chatStore.streamingMessage" class="loading-indicator">
          <div class="spinner-ring"></div>
          <span class="spinner-label">Working on it…</span>
        </div>

        <div v-if="chatStore.progressLines.length > 0" class="streaming-progress">
          <div class="streaming-progress-header" @click="showStreamingProgress = !showStreamingProgress">
            <span>Agent Progress ({{ chatStore.progressLines.length }})</span>
            <svg :class="['progress-chevron', { open: showStreamingProgress }]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </div>
          <div v-if="showStreamingProgress" class="streaming-progress-log">
            <div v-for="(line, idx) in chatStore.progressLines" :key="idx" class="streaming-progress-line">
              <span class="progress-time">{{ line.time }}</span>
              <span class="progress-text">{{ line.text }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="input-container">
        <form @submit.prevent="submitMessage" class="input-form">
          <input
            v-model="inputMessage"
            type="text"
            placeholder="Ask about markets, strategies, timing windows..."
            class="input-field"
          />
          <button v-if="chatStore.isLoading" type="button" class="stop-btn" @click="chatStore.stopStreaming()" title="Stop generating">
            <svg viewBox="0 0 24 24" fill="currentColor" stroke="none">
              <rect x="6" y="6" width="12" height="12" rx="2"></rect>
            </svg>
          </button>
          <button v-else type="submit" class="send-btn" :disabled="!inputMessage.trim()">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="22" y1="2" x2="11" y2="13"></line>
              <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
            </svg>
          </button>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, nextTick, watch, onMounted, onBeforeUnmount } from 'vue';
import { useChatStore } from '../stores/chat';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import ChartWrapper from '../components/ChartWrapper.vue';

const chatStore = useChatStore();
const inputMessage = ref('');
const messagesContainer = ref(null);
const showThreadMenu = ref(false);
const showStreamingProgress = ref(true);
const expandedMsgProgress = ref({});

const toggleMsgProgress = (msgId) => {
  expandedMsgProgress.value = {
    ...expandedMsgProgress.value,
    [msgId]: !expandedMsgProgress.value[msgId]
  };
};

const renderedContent = (content) => {
  const raw = marked.parse(content || '');
  return DOMPurify.sanitize(raw);
};

const chartToChartData = (chart) => {
  if (!chart) return { labels: [], datasets: [] };
  return {
    labels: chart.labels || [],
    datasets: (chart.datasets || []).map(ds => ({
      ...ds,
      data: ds.data || [],
    })),
  };
};

const formatTime = (timestamp) => {
  if (!timestamp) return '';
  return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const isUserNearBottom = () => {
  const el = messagesContainer.value;
  if (!el) return true;
  const threshold = 80; // px from bottom
  return el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
};

const scrollToBottom = async () => {
  await nextTick();
  if (messagesContainer.value) {
    messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
  }
};

// Only auto-scroll while streaming if user is near bottom — don't force scroll when user reads earlier content
watch(() => chatStore.streamingMessage, (val) => {
  if (val && chatStore.isLoading && isUserNearBottom()) scrollToBottom();
});

onMounted(() => {
  document.addEventListener('click', closeThreadMenu);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', closeThreadMenu);
});

const closeThreadMenu = () => {
  showThreadMenu.value = false;
};

const handleNewThread = () => {
  showThreadMenu.value = false;
  const name = `Thread ${chatStore.threads.length}`;
  chatStore.createThread(name);
  nextTick(scrollToBottom);
};

const switchToThread = (threadId) => {
  chatStore.switchThread(threadId);
  showThreadMenu.value = false;
  nextTick(scrollToBottom);
};

const handleDeleteThread = (threadId) => {
  chatStore.deleteThread(threadId);
  nextTick(scrollToBottom);
};

const submitMessage = async () => {
  if (!inputMessage.value.trim()) return;
  if (chatStore.isLoading) {
    chatStore.stopStreaming();
  }
  await chatStore.sendMessage(inputMessage.value);
  inputMessage.value = '';
};
</script>

<style scoped>
.market-chat-page {
  height: calc(100vh - 40px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
}

.chat-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: var(--bg-secondary);
  border-radius: var(--radius-lg);
  border: 1px solid var(--border-color);
  overflow: hidden;
  min-height: 0;
}

.chat-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-lg);
  border-bottom: 1px solid var(--border-color);
  gap: var(--spacing-md);
  flex-wrap: wrap;
}

.chat-header-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.page-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  white-space: nowrap;
}

.chat-thread-controls {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.thread-dropdown-wrapper {
  position: relative;
}

.thread-selector-btn {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 0.65rem;
  background: var(--bg-tertiary, rgba(0,0,0,0.03));
  border: 1px solid var(--border-color);
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.82rem;
  color: var(--text-primary);
  transition: border-color 0.15s;
}

.thread-selector-btn:hover {
  border-color: var(--accent-gold, #d97706);
}

.thread-selector-name {
  font-weight: 500;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.thread-selector-count {
  font-size: 0.7rem;
  color: var(--text-dim, #94a3b8);
  background: rgba(0,0,0,0.06);
  padding: 0.1rem 0.4rem;
  border-radius: 6px;
}

.chevron {
  width: 14px;
  height: 14px;
  color: var(--text-dim, #94a3b8);
}

.thread-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  background: #fff;
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  min-width: 220px;
  z-index: 200;
  overflow: hidden;
}

.thread-dropdown-section {
  padding: 0.4rem 0;
}

.thread-dropdown-item {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.5rem 0.85rem;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.85rem;
  color: var(--text-primary, #1e293b);
  transition: background 0.12s;
  gap: 0.5rem;
}

.thread-dropdown-item:hover {
  background: rgba(0, 0, 0, 0.04);
}

.thread-dropdown-item.active {
  background: var(--accent-dim, rgba(240, 185, 11, 0.08));
  color: var(--accent, #d97706);
  font-weight: 600;
}

.thread-dropdown-item-name {
  flex: 1;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.thread-dropdown-item-count {
  font-size: 0.72rem;
  color: var(--text-dim, #94a3b8);
  background: rgba(0, 0, 0, 0.05);
  padding: 0.1rem 0.45rem;
  border-radius: 8px;
}

.thread-delete-btn {
  background: none;
  border: none;
  color: var(--text-dim, #94a3b8);
  cursor: pointer;
  font-size: 0.85rem;
  padding: 0 0.15rem;
  line-height: 1;
  border-radius: 3px;
  transition: color 0.12s, background 0.12s;
}

.thread-delete-btn:hover {
  color: var(--danger, #ef4444);
  background: rgba(239, 68, 68, 0.08);
}

.new-thread-btn {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.35rem 0.65rem;
  background: var(--accent-gold, #f0b90b);
  border: 1px solid var(--accent-gold, #f0b90b);
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.82rem;
  font-weight: 600;
  color: #000;
  transition: all 0.15s;
  flex-shrink: 0;
}

.new-thread-btn svg {
  width: 14px;
  height: 14px;
}

.new-thread-btn:hover {
  background: #e5c445;
  border-color: #e5c445;
}

.chat-settings {
  display: flex;
  gap: var(--spacing-sm);
}

.setting-select {
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  color: var(--text-primary);
  padding: var(--spacing-sm) var(--spacing-md);
  font-size: 0.9rem;
  cursor: pointer;
}

.setting-select:focus {
  outline: none;
  border-color: var(--accent-gold);
}

.messages-container {
  flex: 1;
  overflow-y: auto;
  padding: var(--spacing-lg);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.message {
  display: flex;
  gap: var(--spacing-sm);
  max-width: 85%;
}

.message.user {
  align-self: flex-end;
  flex-direction: row-reverse;
}

.message.assistant {
  align-self: flex-start;
}

.message-avatar {
  font-size: 0.75rem;
  font-weight: 700;
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-tertiary, rgba(0,0,0,0.05));
  color: var(--text-secondary);
}

.message.user .message-avatar {
  background: var(--accent-gold, #f0b90b);
  color: #000;
}

.message.assistant .message-avatar {
  background: rgba(16, 185, 129, 0.12);
  color: var(--bullish, #10b981);
}

.message-content {
  background: var(--bg-tertiary);
  border-radius: var(--radius-lg);
  padding: var(--spacing-md);
  max-width: 100%;
  min-width: 0;
  overflow-wrap: break-word;
  word-break: break-word;
}

.message.user .message-content {
  background: var(--accent-gold);
  color: #000;
}

.message-text {
  font-size: 0.9rem;
  line-height: 1.5;
  word-break: break-word;
}

.message-text.markdown {
  color: var(--text-primary);
}

.message-text.markdown :deep(p) {
  margin-bottom: var(--spacing-sm);
}

.message-text.markdown :deep(p:last-child) {
  margin-bottom: 0;
}

.message-text.markdown :deep(code) {
  background: var(--bg-primary);
  padding: 2px 6px;
  border-radius: var(--radius-sm);
  font-family: var(--font-mono);
  font-size: 0.85em;
}

.message-text.markdown :deep(pre) {
  background: var(--bg-primary);
  padding: var(--spacing-md);
  border-radius: var(--radius-md);
  overflow-x: auto;
  margin: var(--spacing-sm) 0;
}

.message-text.markdown :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin: 0.5rem 0;
  font-size: 0.82rem;
  display: block;
  overflow-x: auto;
}

.message-text.markdown :deep(thead) {
  background: var(--bg-tertiary, rgba(0, 0, 0, 0.04));
}

.message-text.markdown :deep(th) {
  padding: 0.5rem 0.75rem;
  text-align: left;
  font-weight: 600;
  border-bottom: 2px solid var(--border-color, #e2e8f0);
  white-space: nowrap;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: var(--text-secondary, #64748b);
}

.message-text.markdown :deep(td) {
  padding: 0.4rem 0.75rem;
  border-bottom: 1px solid var(--border-color, #e2e8f0);
  color: var(--text-primary, #1e293b);
  white-space: nowrap;
}

.message-text.markdown :deep(tbody tr:hover) {
  background: var(--bg-tertiary, rgba(0, 0, 0, 0.02));
}

.message-time {
  display: block;
  font-size: 0.7rem;
  color: var(--text-muted);
  margin-top: var(--spacing-xs);
}

.message-charts {
  margin-top: var(--spacing-sm);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.message-charts :deep(.chart-wrapper) {
  background: rgba(252, 250, 246, 0.98);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: 1rem;
  box-shadow: 0 2px 8px rgba(29, 36, 48, 0.06);
  backdrop-filter: blur(8px);
}

.message-charts :deep(.chart-wrapper canvas) {
  max-height: 320px;
}

.msg-progress {
  margin-top: 0.6rem;
  background: #1a1610;
  border: 1px solid rgba(182, 138, 46, 0.15);
  border-radius: 8px;
  padding: 0.35rem 0.55rem;
  font-size: 0.72rem;
  min-height: auto;
  max-height: 4.5rem;
  overflow-y: auto;
  box-shadow: inset 0 1px 6px rgba(0,0,0,0.25);
  font-family: 'JetBrains Mono', 'Fira Code', 'IBM Plex Mono', monospace;
}

.msg-progress-header {
  font-weight: 800;
  margin-bottom: 0.35rem;
  color: rgba(182, 138, 46, 0.7);
  font-size: 0.58rem;
  text-transform: uppercase;
  letter-spacing: 0.16em;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  user-select: none;
  padding: 0.15rem 0;
}

.msg-progress-header:hover {
  color: rgba(182, 138, 46, 1);
}

.msg-progress-lines {
  border-top: 1px solid rgba(182, 138, 46, 0.15);
  padding-top: 0.3rem;
}

.progress-chevron {
  width: 14px;
  height: 14px;
  color: rgba(182, 138, 46, 0.5);
  transition: transform 0.15s;
  flex-shrink: 0;
}

.progress-chevron.open {
  transform: rotate(180deg);
}

.msg-progress-line {
  display: flex;
  gap: 0.55rem;
  padding: 0.15rem 0;
  align-items: flex-start;
}

.msg-progress-line::before {
  content: '';
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: var(--gold);
  box-shadow: 0 0 10px var(--gold);
  flex: 0 0 auto;
  margin-top: 0.35rem;
}

.msg-progress-time {
  color: rgba(182, 138, 46, 0.5);
  font-family: var(--font-mono);
  font-size: 0.62rem;
  font-weight: 700;
  flex-shrink: 0;
}

.msg-progress-text {
  color: #d4c4a8;
  word-break: break-word;
  flex: 1;
}

.streaming-progress {
  background: #1a1610;
  border: 1px solid rgba(182, 138, 46, 0.2);
  border-radius: 12px;
  padding: 0.8rem 0.85rem;
  font-size: 0.78rem;
  margin-top: 0.5rem;
  min-height: 6.5rem;
  max-height: 12rem;
  overflow-y: auto;
  box-shadow: inset 0 2px 15px rgba(0,0,0,0.4), 0 8px 24px rgba(0,0,0,0.1);
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  font-family: 'JetBrains Mono', 'Fira Code', 'IBM Plex Mono', monospace;
}

.streaming-progress-header {
  font-size: 0.58rem;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(182, 138, 46, 0.7);
  margin-bottom: 0.2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.55rem;
  cursor: pointer;
  user-select: none;
}

.streaming-progress-header:hover {
  color: rgba(182, 138, 46, 1);
}

.streaming-progress-log {
  display: flex;
  flex-direction: column;
  gap: 0.42rem;
  width: 100%;
}

.streaming-progress-line {
  font-size: 0.68rem;
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  line-height: 1.45;
  width: 100%;
  text-align: left !important;
  animation: fadeInSlide 0.2s ease-out;
}

@keyframes fadeInSlide {
  from { opacity: 0; transform: translateX(-4px); }
  to { opacity: 1; transform: translateX(0); }
}

.streaming-progress-line::before {
  content: '';
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: var(--gold);
  box-shadow: 0 0 10px var(--gold);
  flex: 0 0 auto;
  margin-top: 0.35rem;
}

.progress-time {
  color: rgba(182, 138, 46, 0.5);
  font-size: 0.62rem;
  font-weight: 700;
  text-align: right;
  margin-left: auto;
  background: rgba(182, 138, 46, 0.05);
  padding: 0.1rem 0.4rem;
  border-radius: 4px;
  flex: 0 0 auto;
}

.progress-text {
  flex: 1;
  color: #d4c4a8;
  text-align: left !important;
  white-space: normal;
  overflow-wrap: anywhere;
}

.loading-indicator {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0;
  align-self: flex-start;
}

.spinner-ring {
  width: 20px;
  height: 20px;
  border: 2.5px solid rgba(0, 0, 0, 0.1);
  border-top-color: var(--accent, #d97706);
  border-radius: 50%;
  animation: spin-ring 0.8s linear infinite;
}

.spinner-label {
  font-size: 0.82rem;
  color: var(--text-secondary);
  font-style: italic;
}

@keyframes spin-ring {
  to { transform: rotate(360deg); }
}

.input-container {
  border-top: 1px solid var(--border-color);
  padding: var(--spacing-lg);
}

.input-form {
  display: flex;
  gap: var(--spacing-sm);
}

.input-field {
  flex: 1;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  color: var(--text-primary);
  padding: var(--spacing-md);
  font-size: 0.9rem;
  outline: none;
  transition: border-color var(--transition-fast);
}

.input-field:focus {
  border-color: var(--accent-gold);
}

.input-field:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.send-btn {
  background: var(--accent-gold);
  border: none;
  border-radius: var(--radius-md);
  color: #000;
  cursor: pointer;
  padding: var(--spacing-md);
  font-size: 1rem;
  transition: all var(--transition-fast);
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 50px;
}

.send-btn svg {
  width: 18px;
  height: 18px;
}

.send-btn:hover:not(:disabled) {
  background: #e5c445;
}

.send-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.stop-btn {
  background: linear-gradient(180deg, #ea580c 0%, #c2410c 100%);
  border: none;
  border-radius: var(--radius-md);
  color: #fff;
  cursor: pointer;
  padding: var(--spacing-md);
  font-size: 1rem;
  transition: all var(--transition-fast);
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 50px;
}

.stop-btn svg {
  width: 18px;
  height: 18px;
}

.stop-btn:hover {
  background: linear-gradient(180deg, #dc4d0b 0%, #9a3412 100%);
}

@media (max-width: 768px) {
  .market-chat-page {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    height: 100dvh;
    z-index: 50;
  }

  .chat-container {
    border-radius: 0;
    border: none;
    flex: 1;
    min-height: 0;
  }

  .chat-header {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: 0.5rem 0.75rem;
    gap: 0.3rem;
    flex-wrap: nowrap;
    flex-shrink: 0;
  }

  .chat-header-left {
    flex-wrap: nowrap;
    width: auto;
    min-width: 0;
    gap: 0.4rem;
  }

  .page-title {
    font-size: 0.9rem;
    margin-right: 0.25rem;
  }

  .chat-thread-controls {
    gap: 0.25rem;
  }

  .thread-selector-btn {
    padding: 0.25rem 0.45rem;
    font-size: 0.75rem;
  }

  .thread-selector-name {
    max-width: 80px;
  }

  .new-thread-btn {
    padding: 0.25rem 0.45rem;
    font-size: 0.75rem;
  }

  .chat-settings {
    width: auto;
    flex-shrink: 0;
  }

  .setting-select {
    padding: 0.25rem 0.5rem;
    font-size: 0.8rem;
  }

  .messages-container {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    padding: 0.75rem;
    gap: 0.6rem;
  }

  .message {
    max-width: 92%;
    gap: 0.4rem;
  }

  .message-avatar {
    width: 24px;
    height: 24px;
    font-size: 0.65rem;
    flex-shrink: 0;
  }

  .message-content {
    padding: 0.5rem 0.65rem;
    border-radius: 12px;
    min-width: 0;
  }

  .message-text {
    font-size: 0.88rem;
    line-height: 1.55;
    word-break: break-word;
    overflow-wrap: break-word;
  }

  .message-time {
    font-size: 0.65rem;
    margin-top: 0.15rem;
  }

  .input-container {
    padding: 0.5rem 0.75rem;
    flex-shrink: 0;
    background: var(--bg-secondary);
    padding-bottom: max(0.5rem, env(safe-area-inset-bottom));
  }

  .input-field {
    font-size: 16px;
    padding: 0.55rem 0.7rem;
    min-height: 40px;
  }

  .send-btn {
    min-width: 44px;
    padding: 0.55rem;
  }
}
</style>
